import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, CheckCircle2, KeyRound, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) setError("Link di reset mancante o incompleto. Richiedi di nuovo il reset dall'area login.");
  }, [token]);

  const validate = () => {
    if (password.length < 8) return "La password deve avere almeno 8 caratteri.";
    if (!/[A-Z]/.test(password)) return "La password deve contenere almeno una lettera maiuscola.";
    if (!/[a-z]/.test(password)) return "La password deve contenere almeno una lettera minuscola.";
    if (!/[0-9]/.test(password)) return "La password deve contenere almeno un numero.";
    if (password !== confirm) return "Le due password non coincidono.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/reset-password`,
        null,
        { params: { token, new_password: password } }
      );
      setSuccess(true);
      setTimeout(() => navigate("/"), 2500);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Impossibile reimpostare la password. Il link potrebbe essere scaduto.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50 flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 sm:p-8 border border-slate-200">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-full bg-orange-100 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Reimposta password</h1>
            <p className="text-xs text-slate-500">Inserisci la nuova password del tuo account.</p>
          </div>
        </div>

        {success ? (
          <div className="space-y-4 text-center" data-testid="reset-password-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h2 className="text-lg font-semibold text-slate-800">Password aggiornata</h2>
            <p className="text-sm text-slate-600">
              La tua password è stata reimpostata correttamente. Verrai reindirizzato alla home tra pochi istanti.
            </p>
            <button
              onClick={() => navigate("/")}
              className="w-full px-4 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white rounded-lg font-medium"
              data-testid="reset-password-back-home"
            >
              Vai alla home
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Nuova password</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={!token || submitting}
                  autoComplete="new-password"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  aria-label={show ? "Nascondi password" : "Mostra password"}
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Almeno 8 caratteri con maiuscola, minuscola e numero.
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Conferma nuova password</label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={!token || submitting}
                  autoComplete="new-password"
                  data-testid="reset-password-confirm-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  aria-label={showConfirm ? "Nascondi password" : "Mostra password"}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3"
                data-testid="reset-password-error"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={!token || submitting}
              className="w-full px-4 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white rounded-lg font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="reset-password-submit-btn"
            >
              {submitting ? "Aggiornamento in corso..." : "Reimposta password"}
            </button>

            <button
              type="button"
              onClick={() => navigate("/")}
              className="w-full text-sm text-slate-500 hover:text-slate-700"
              data-testid="reset-password-cancel"
            >
              Torna alla home
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
